import { useRef, useState } from "react";
import { useT } from "@/i18n";
import { cn } from "@/lib/utils";
import { ApiClientError } from "@/lib/api";
import { Tooltip } from "../tooltip";

/**
 * The inline-editing kit's shared parts: the hover surface, the box the display and the
 * input both wear, and the commit/cancel state machine every field uses.
 *
 * **The model.** Click a value and it becomes an input, autofocused. Enter or blur commits
 * that one field as one request carrying the version it was read at; Escape cancels and
 * writes nothing; a field-local spinner runs while the write is in flight; the field returns
 * to display mode on the server's committed value. Editing three fields therefore costs
 * three requests and three audit rows — the timeline's consecutive grouping absorbs that,
 * and each write carries the *latest* version, so the conflict window per write is smaller
 * than a whole-form submit's.
 *
 * **The affordance is the surface.** An editable value renders inside an invisible hit area
 * that shows a soft background on hover; a read-only one gets no hover and no pointer
 * cursor. The presence of the highlight *is* the discoverability, and its absence is the
 * permission signal — which is why there are no pencil icons anywhere in here.
 *
 * See `.claude/docs/ui-patterns.md`, "Editing from a detail".
 */

/**
 * One height per size, worn by **both** the display surface and the input.
 *
 * Load-bearing rather than tidy: without it a row grows by a few pixels the moment a value
 * flips to an input, and every fact below it jumps. The hero header is read in meetings.
 */
const SIZE_CLASSES = {
  sm: "h-7 text-sm",
  lg: "h-9 text-xl font-semibold",
} as const;

export type InlineSize = keyof typeof SIZE_CLASSES;

export function sizeClasses(size: InlineSize): string {
  return SIZE_CLASSES[size];
}

/**
 * The display surface's own height — `min-h`, not the input's fixed `h`.
 *
 * A value that does not fit the hero's narrow columns on one line (a long manager name, a
 * seven-figure amount with its currency prefix) wraps rather than truncates to an
 * unreadable "Lucie Ma…", so the box has to be allowed to grow for it. The invariant above
 * — display and input the same height — still holds for the input itself, which is
 * rendered separately from `ViewSurface` and keeps `SIZE_CLASSES`'s fixed height verbatim;
 * only an already-wrapped value's one-time click into edit mode ever changes height, which
 * is the field the reader just looked at, not one further down the row.
 */
const DISPLAY_MIN_HEIGHT = {
  sm: "min-h-7 text-sm",
  lg: "min-h-9 text-xl font-semibold",
} as const;

/** The box an inline input wears, so it occupies exactly the display surface's space. */
export function inputClasses(size: InlineSize): string {
  return cn(
    "border-input bg-background focus-visible:ring-ring -mx-1 w-full min-w-0 rounded border px-1 focus-visible:ring-2 focus-visible:outline-none disabled:opacity-60",
    SIZE_CLASSES[size],
  );
}

/**
 * A value that can be clicked into an input — or, when it cannot, a plain value.
 *
 * The read-only branch renders **no button at all**, not a disabled one: an affordance that
 * refuses is worse than an absent affordance, and "is this editable by me?" is answerable by
 * looking. `disabled` is the other case — the value *is* editable, but writes are paused
 * (the connection dropped), so the button stays and says why.
 */
export function ViewSurface({
  label,
  editable,
  disabled = false,
  saving = false,
  size = "sm",
  onActivate,
  className,
  children,
}: {
  /** The field's name, for the hover tooltip. There is no visible label in the hero. */
  label: string;
  editable: boolean;
  disabled?: boolean;
  saving?: boolean;
  size?: InlineSize;
  onActivate: () => void;
  className?: string;
  children: React.ReactNode;
}) {
  const t = useT();
  const box = cn("flex max-w-full items-center gap-1.5", DISPLAY_MIN_HEIGHT[size], className);

  if (!editable) {
    // No hover, no pointer, no button semantics — nothing here suggests it can be changed.
    return <span className={cn(box, "cursor-default")}>{children}</span>;
  }

  return (
    // The shared tooltip, never a native `title`: it says what clicking does — or why it
    // currently does nothing — and it reaches a keyboard, which the browser's own never did.
    // No `aria-label` on the button: the value itself is its accessible name, which is what a
    // screen-reader user needs to hear.
    <Tooltip content={disabled ? t("common.paused") : t("inline.clickToEdit", label)}>
      <button
        type="button"
        disabled={disabled || saving}
        onClick={onActivate}
        className={cn(
          box,
          "hover:bg-foreground/10 focus-visible:ring-ring -mx-1 cursor-pointer rounded px-1 text-left focus-visible:ring-2 focus-visible:outline-none",
          "disabled:cursor-default disabled:hover:bg-transparent",
        )}
      >
        {/* Wraps at word boundaries rather than truncating — see `DISPLAY_MIN_HEIGHT`
            above. Not `break-words`: forcing a break *inside* a word is how "CZK
            2,100,000.00" gets split after the "000." instead of after the currency code,
            and every value here (a date, an amount, a two-word name) already has a space
            to wrap at. */}
        <span className="min-w-0">{children}</span>
        {saving && <InlineSpinner />}
      </button>
    </Tooltip>
  );
}

/** The field-local spinner. Per field, deliberately: one field's write is not the modal's. */
export function InlineSpinner() {
  const t = useT();

  return (
    <span
      role="status"
      aria-label={t("common.savingStatus")}
      className="border-muted-foreground/40 border-t-primary h-3 w-3 shrink-0 animate-spin rounded-full border-2"
    />
  );
}

/** The message a refused write leaves under the field. A toast is never its only record. */
export function InlineError({ message }: { message: string }) {
  return (
    <p className="text-destructive mt-0.5 text-xs" role="alert">
      {message}
    </p>
  );
}

/**
 * The commit/cancel state machine, written once.
 *
 * Three properties it exists to guarantee, each of which is a rule from the spec rather
 * than an implementation detail:
 *
 * - **A no-op blur writes nothing.** Clicking into a value and out of it again is not an
 *   edit, and must not spend a version bump or an audit row on saying so.
 * - **Enter followed by blur commits once.** Committing on Enter moves focus, which fires
 *   blur — so a `settled` latch closes the field to further commits until it reopens.
 * - **Escape writes nothing at all**, which is the same rule cancelling a dialog obeys.
 */
export function useInlineEdit<T>({
  value,
  equals,
  onCommit,
  validate,
  initiallyEditing = false,
}: {
  value: T;
  /** When the draft is not different from the committed value, nothing is sent. */
  equals: (a: T, b: T) => boolean;
  onCommit: (next: T) => Promise<unknown>;
  /** Returns a message to refuse the value with, or `undefined` to let it through. */
  validate?: (next: T) => string | undefined;
  /**
   * Mount already open. For an affordance that *is* the click — "+ Add note", which the
   * parent replaces with a mounted, focused textarea rather than a box to click again.
   */
  initiallyEditing?: boolean;
}) {
  const [editing, setEditing] = useState(initiallyEditing);
  const [draft, setDraft] = useState<T>(value);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const settled = useRef(false);

  const begin = (): void => {
    setDraft(value);
    setError(undefined);
    settled.current = false;
    setEditing(true);
  };

  const cancel = (): void => {
    settled.current = true;
    setEditing(false);
    setError(undefined);
  };

  const commit = async (next: T): Promise<void> => {
    if (settled.current) return;
    settled.current = true;

    if (equals(next, value)) {
      // Unchanged: leave edit mode and send nothing. This is the no-op blur.
      setEditing(false);
      return;
    }

    /**
     * Local refusal, before anything is sent.
     *
     * Some fields have a rule the server also enforces but the reader should not have to make a
     * request to hear — a lost reason's minimum, for one. Without this a caller could only
     * signal by throwing, and a plain `Error` fell into the branch below that closes the field
     * and *discards the typing*, which is the worst of the three outcomes.
     */
    const refusal = validate?.(next);
    if (refusal !== undefined) {
      settled.current = false;
      setError(refusal);
      return;
    }

    setSaving(true);
    try {
      await onCommit(next);
      setEditing(false);
    } catch (failure) {
      if (failure instanceof ApiClientError && failure.isValidation) {
        // The field stays open carrying its message: a toast is never the only record of a
        // validation failure, and throwing the user's typing away would be worse than both.
        settled.current = false;
        setError(firstFieldError(failure) ?? failure.message);
      } else {
        // A stale write, or anything else: the shared reporter has toasted it and re-read
        // the record, so the field returns to display mode showing the server's value.
        setEditing(false);
      }
    } finally {
      setSaving(false);
    }
  };

  return { editing, draft, setDraft, saving, error, begin, cancel, commit };
}

function firstFieldError(error: ApiClientError): string | undefined {
  for (const messages of Object.values(error.fieldErrors)) {
    if (messages[0]) return messages[0];
  }
  return undefined;
}

/**
 * The keyboard contract shared by every single-line inline field: Enter commits, Escape
 * cancels. A textarea overrides it — there, Enter is a newline.
 */
export function singleLineKeys<T>(
  { commit, cancel }: { commit: (next: T) => Promise<void>; cancel: () => void },
  next: () => T,
): (event: React.KeyboardEvent) => void {
  return (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      void commit(next());
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      // Stopped here so Escape does not also close the modal the field lives in — the
      // reader asked to abandon an edit, not to leave the record.
      event.stopPropagation();
      cancel();
    }
  };
}
